import { useState, useEffect, useCallback } from "react";
import {
  BarChart3, TrendingUp, AlertTriangle, RefreshCw, Download,
  ChevronDown, Loader2, CheckCircle2, DollarSign, FileSpreadsheet,
  Building2, ArrowUpRight, ArrowDownRight, Filter, Grid3X3
} from "lucide-react";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { formatPYGCompact } from "../utils/format";
import { toast } from "sonner";
import { Skeleton } from "../components/ui/skeleton";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  LineChart, Line, ResponsiveContainer, Cell
} from "recharts";

// ─── Helpers ──────────────────────────────────────────────

const PCT_COLOR = (pct) => {
  if (pct >= 100) return "text-red-600";
  if (pct >= 85) return "text-amber-600";
  if (pct >= 60) return "text-yellow-600";
  return "text-emerald-600";
};

const HEALTH_BADGE = (pct) => {
  if (pct >= 100) return { label: "Superado", cls: "bg-red-100 text-red-700 border-red-200" };
  if (pct >= 85) return { label: "En Riesgo", cls: "bg-amber-100 text-amber-700 border-amber-200" };
  if (pct >= 60) return { label: "Controlado", cls: "bg-yellow-100 text-yellow-700 border-yellow-200" };
  return { label: "Saludable", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" };
};

function SemBarChart({ data }) {
  if (!data || data.length === 0) return <p className="text-xs text-gray-400 text-center py-6">Sin datos de categorías</p>;
  const chartData = data.slice(0, 8);
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={chartData} margin={{ top: 4, right: 8, bottom: 40, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
        <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#6B7280" }} angle={-30} textAnchor="end" interval={0} />
        <YAxis tickFormatter={v => `${(v / 1_000_000).toFixed(0)}M`} tick={{ fontSize: 10, fill: "#6B7280" }} />
        <Tooltip formatter={(v) => [`Gs. ${Number(v).toLocaleString("es-PY")}`, "Ejecutado"]} />
        <Bar dataKey="amount" name="Ejecutado" radius={[4, 4, 0, 0]}>
          {chartData.map((entry, index) => (
            <Cell key={`bar-${index}`} fill={entry.color || "#2E6DA4"} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function MonthlyFlowChart({ data }) {
  if (!data || data.length === 0) return <p className="text-xs text-gray-400 text-center py-6">Sin datos de flujo mensual</p>;
  const chartData = data.map(d => ({
    ...d,
    mes: d.mes.slice(5), // Show only MM from YYYY-MM
  }));
  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 8, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
        <XAxis dataKey="mes" tick={{ fontSize: 10, fill: "#6B7280" }} />
        <YAxis tickFormatter={v => `${(v / 1_000_000).toFixed(0)}M`} tick={{ fontSize: 10, fill: "#6B7280" }} />
        <Tooltip formatter={(v, name) => [`Gs. ${Number(v).toLocaleString("es-PY")}`, name === "ejecutado" ? "Gasto" : "Ingreso"]} />
        <Legend formatter={v => v === "ejecutado" ? "Gastos" : "Ingresos"} />
        <Line type="monotone" dataKey="ejecutado" stroke="#EF4444" strokeWidth={2} dot={{ r: 3 }} />
        <Line type="monotone" dataKey="ingresos" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ─── GLOBAL VIEW ──────────────────────────────────────────
// Fix CN (2026-09-13): "acá los fondos disponibles deberían estirar de forma automática
// de los presupuestos existentes... de esta manera tendremos en un pantallazo el estado
// financiero de todas las obras con parámetros e indicadores clave a la vista" (Edson,
// 13/09). La tabla pasa a listar OBRAS (no proyectos sueltos) y trae las columnas que
// pidió: presupuesto de venta, certificado, cobrado, costo proyectado y ejecutado por
// rubro, y los tres rendimientos. Todo en neto (sin IVA), como el resto de la gestión.
// Fix CS (2026-09-14): "en el filtro de clientes aparecen los clientes en forma
// duplicada. Los únicos y verdaderos válidos son los que se cargan en el apartado
// CLIENTES del módulo INGENIERIA" (Heber, 14/09). El filtro salía del texto libre que
// cada obra tiene guardado, por eso convivían "AGB CONSTRUCTORA" y "AGB CONSTRUCTORA
// S.A". Ahora la lista es el catálogo y, para filtrar, se comparan nombres
// normalizados (sin mayúsculas, acentos, puntos ni S.A./S.R.L./E.A.S.), así una obra
// cargada con la variante vieja igual cae bajo su cliente real.
const normalizarCliente = (s) => String(s || "").toLowerCase()
  .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .replace(/\b(s\.?\s?a\.?\s?s?|s\.?\s?r\.?\s?l\.?|e\.?\s?a\.?\s?s\.?|ltda?)\b/g, "")
  .replace(/[^a-z0-9]/g, "").trim();

const RENDIMIENTO_COLOR = (pct) => {
  if (!pct) return "text-gray-400";
  if (pct >= 130) return "text-emerald-600";
  if (pct >= 100) return "text-yellow-600";
  return "text-red-600";
};

function GlobalView({ onSelectProject }) {
  const [obras, setObras] = useState([]);
  const [totales, setTotales] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("certificado");
  const [q, setQ] = useState("");
  const [obraFiltro, setObraFiltro] = useState("");
  const [clienteFiltro, setClienteFiltro] = useState("");
  const [catalogoClientes, setCatalogoClientes] = useState([]);   // Fix CS (2026-09-14)

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [res, cliRes] = await Promise.all([
        axios.get(`${API}/finance/por-obra`, { withCredentials: true }),
        axios.get(`${API}/listas/clientes`, { withCredentials: true }).catch(() => ({ data: [] })),   // Fix CS (2026-09-14)
      ]);
      setObras(res.data?.obras || []);
      setTotales(res.data?.totales || null);
      setCatalogoClientes(Array.isArray(cliRes.data) ? cliRes.data : (cliRes.data?.clientes || []));
    } catch { toast.error("Error al cargar el estado financiero de las obras"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const texto = q.trim().toLowerCase();
  const filtradas = obras.filter(o =>
    (!texto || [o.obra_nombre, o.codigo, o.cliente, o.presupuesto_numero].some(v => String(v || "").toLowerCase().includes(texto)))
    && (!obraFiltro || o.obra_id === obraFiltro)
    && (!clienteFiltro || normalizarCliente(o.cliente) === normalizarCliente(clienteFiltro)));   // Fix CS (2026-09-14)

  const sorted = [...filtradas].sort((a, b) => {
    if (sortBy === "rendimiento") return (a.rend_gral || 0) - (b.rend_gral || 0);
    if (sortBy === "ejecutado") return b.costo_ejecutado - a.costo_ejecutado;
    if (sortBy === "presupuesto") return b.presupuesto_venta - a.presupuesto_venta;
    return b.certificado - a.certificado;
  });

  // Fix CS (2026-09-14): el catálogo manda; si todavía no cargó, se cae a lo de antes.
  const clientes = catalogoClientes.length
    ? [...new Set(catalogoClientes.map(c => c.cliente).filter(Boolean))].sort()
    : [...new Set(obras.map(o => o.cliente).filter(Boolean))].sort();
  const suma = (campo) => filtradas.reduce((s, o) => s + (Number(o[campo]) || 0), 0);
  const kpis = [
    { label: "Presupuesto (Venta)", value: formatPYGCompact(suma("presupuesto_venta")), icon: DollarSign, color: "blue" },
    { label: "Certificado a la fecha", value: formatPYGCompact(suma("certificado")), icon: FileSpreadsheet, color: "emerald" },
    { label: "Cobrado a la fecha", value: formatPYGCompact(suma("cobrado")), icon: CheckCircle2, color: "emerald" },
    { label: "Costo ejecutado", value: formatPYGCompact(suma("costo_ejecutado")), icon: TrendingUp, color: "red" },
    { label: "Obras con costo excedido", value: filtradas.filter(o => o.alerta).length, icon: AlertTriangle, color: "amber" },
  ];

  const celdaMonto = (monto, pct) => (
    <td className="px-3 py-3 text-right whitespace-nowrap">
      <span className="text-sm font-semibold text-gray-900 tabular-nums">{formatPYGCompact(monto)}</span>
      {pct !== undefined && <span className="block text-[10px] text-gray-400 tabular-nums">{pct}%</span>}
    </td>
  );

  return (
    <div className="space-y-5">
      {/* Global KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="finanzas-obra-kpis">
        {kpis.map(c => (
          <div key={c.label} className="bg-white rounded-xl border border-gray-200/60 p-4 shadow-sm">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${c.color === "blue" ? "bg-blue-50" : c.color === "red" ? "bg-red-50" : c.color === "amber" ? "bg-amber-50" : "bg-emerald-50"}`}>
              <c.icon size={18} className={c.color === "blue" ? "text-blue-600" : c.color === "red" ? "text-red-600" : c.color === "amber" ? "text-amber-600" : "text-emerald-600"} />
            </div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{c.label}</p>
            <p className="text-xl font-bold text-gray-900 mt-0.5">{loading ? "…" : c.value}</p>
          </div>
        ))}
      </div>

      {/* Filtros + orden */}
      <div className="flex items-center gap-2 flex-wrap">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar obra, código o cliente"
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-xs min-w-[220px]" data-testid="finanzas-obra-buscar" />
        <select value={obraFiltro} onChange={e => setObraFiltro(e.target.value)}
          className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white" data-testid="finanzas-obra-select">
          <option value="">Todas las obras</option>
          {obras.map(o => <option key={o.obra_id} value={o.obra_id}>{o.obra_nombre}</option>)}
        </select>
        <select value={clienteFiltro} onChange={e => setClienteFiltro(e.target.value)}
          className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white" data-testid="finanzas-cliente-select">
          <option value="">Todos los clientes</option>
          {clientes.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide ml-2">Ordenar por:</span>
        {[{ v: "certificado", l: "Certificado" }, { v: "ejecutado", l: "Mayor Gasto" },
          { v: "presupuesto", l: "Presupuesto" }, { v: "rendimiento", l: "Menor Rendimiento" }].map(opt => (
          <button key={opt.v} onClick={() => setSortBy(opt.v)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all ${sortBy === opt.v ? "bg-[#2E6DA4] text-white border-[#2E6DA4]" : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"}`}
            data-testid={`finanzas-orden-${opt.v}`}>
            {opt.l}
          </button>
        ))}
      </div>

      {/* Tabla de obras */}
      <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50/50 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Estado financiero por obra</p>
          <p className="text-[11px] text-gray-400">Montos netos, sin IVA · Rendimiento = Certificado ÷ Costo ejecutado</p>
          {/* Fix DR (2026-09-22): de dónde sale cada columna de ejecutado, para que no
              haya que preguntarlo. */}
          <p className="text-[11px] text-gray-400 w-full" data-testid="finanzas-origen-ejecutado">
            MAT Ejec. = compras imputadas a la obra · MO Ejec. = jornales y tercerizados cargados en las tareas · ADM Ejec. = caja chica y gastos administrativos del mes
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="finanzas-obra-tabla">
            <thead>
              <tr className="border-b border-gray-100">
                {["Obra", "Presupuesto (Venta)", "Certificado", "Cobrado", "Costo Total Proy.",
                  "MAT Pro", "MO Pro", "ADM Pro", "MAT Ejec.", "MO Ejec.", "ADM Ejec.",
                  "Rend. MAT", "Rend. MO", "Rend. Gral", ""].map(h => (
                  <th key={h} className="text-left px-3 py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? Array.from({ length: 4 }).map((_, i) => (
                <tr key={i} className="border-b border-gray-50">
                  {Array.from({ length: 15 }).map((__, j) => <td key={j} className="px-3 py-3"><Skeleton className="h-4 w-full" /></td>)}
                </tr>
              )) : sorted.length === 0 ? (
                <tr><td colSpan={15} className="text-center py-12 text-gray-400 text-sm">
                  <Building2 size={32} className="mx-auto mb-2 opacity-20" />
                  {obras.length ? "Sin coincidencias con los filtros" : "No hay obras con datos financieros"}
                </td></tr>
              ) : sorted.map(o => (
                <tr key={o.obra_id} className="border-b border-gray-50 hover:bg-gray-50/40 transition-colors" data-testid={`finanzas-fila-${o.obra_id}`}>
                  <td className="px-3 py-3 min-w-[190px]">
                    <p className="text-sm font-semibold text-gray-900">{o.obra_nombre}</p>
                    <p className="text-xs text-gray-400">{o.codigo} · {o.cliente || "—"}</p>
                  </td>
                  {celdaMonto(o.presupuesto_venta)}
                  {celdaMonto(o.certificado, o.pct_certificado)}
                  {celdaMonto(o.cobrado, o.pct_cobrado)}
                  {celdaMonto(o.costo_proyectado)}
                  {celdaMonto(o.mat_pro)}
                  {celdaMonto(o.mo_pro)}
                  {celdaMonto(o.adm_pro)}
                  {celdaMonto(o.mat_ejecutado, o.pct_mat)}
                  {celdaMonto(o.mo_ejecutado, o.pct_mo)}
                  {celdaMonto(o.adm_ejecutado, o.pct_adm)}
                  <td className={`px-3 py-3 text-right text-sm font-bold tabular-nums ${RENDIMIENTO_COLOR(o.rend_mat)}`}>{o.rend_mat}%</td>
                  <td className={`px-3 py-3 text-right text-sm font-bold tabular-nums ${RENDIMIENTO_COLOR(o.rend_mo)}`}>{o.rend_mo}%</td>
                  <td className={`px-3 py-3 text-right text-sm font-bold tabular-nums ${RENDIMIENTO_COLOR(o.rend_gral)}`}>{o.rend_gral}%</td>
                  <td className="px-3 py-3">
                    {o.project_id ? (
                      <button onClick={() => onSelectProject(o.project_id)}
                        className="flex items-center gap-1 text-xs font-semibold text-[#2E6DA4] hover:text-[#245A8A] hover:underline transition-colors whitespace-nowrap"
                        data-testid={`view-project-${o.project_id}`}>
                        Ver detalle <ArrowUpRight size={12} />
                      </button>
                    ) : <span className="text-[11px] text-gray-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
            {!loading && sorted.length > 0 && (
              <tfoot>
                <tr className="bg-gray-50 font-bold text-gray-800 text-sm">
                  <td className="px-3 py-3">Totales ({sorted.length})</td>
                  {["presupuesto_venta", "certificado", "cobrado", "costo_proyectado", "mat_pro", "mo_pro",
                    "adm_pro", "mat_ejecutado", "mo_ejecutado", "adm_ejecutado"].map(campo => (
                    <td key={campo} className="px-3 py-3 text-right tabular-nums whitespace-nowrap" data-testid={`finanzas-total-${campo}`}>
                      {formatPYGCompact(suma(campo))}
                    </td>
                  ))}
                  <td colSpan={4} className="px-3 py-3 text-right text-xs text-gray-500">
                    {totales ? `Rendimiento general: ${totales.rend_gral}%` : ""}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── PROJECT DETAIL VIEW ──────────────────────────────────
function ProjectDetail({ projectId, onBack }) {
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTxn, setLoadingTxn] = useState(false);
  const [catFilter, setCatFilter] = useState("");
  const [exporting, setExporting] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, tRes] = await Promise.all([
        axios.get(`${API}/finance/by-project/${projectId}`, { withCredentials: true }),
        axios.get(`${API}/finance/by-project/${projectId}/transactions`, { withCredentials: true }),
      ]);
      setSummary(sRes.data);
      setTransactions(tRes.data || []);
    } catch { toast.error("Error al cargar datos del proyecto"); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const handleCatFilter = useCallback(async (cat) => {
    setCatFilter(cat);
    setLoadingTxn(true);
    try {
      const params = cat ? `?category=${cat}` : "";
      const res = await axios.get(`${API}/finance/by-project/${projectId}/transactions${params}`, { withCredentials: true });
      setTransactions(res.data || []);
    } catch { toast.error("Error al filtrar transacciones"); }
    finally { setLoadingTxn(false); }
  }, [projectId]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/finance/by-project/${projectId}/export-excel`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Finanzas_Obra_${summary?.project_name?.replace(/\s/g, "_") || projectId}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel exportado correctamente");
    } catch { toast.error("Error al exportar"); }
    finally { setExporting(false); }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64 rounded-xl" />
        <div className="grid grid-cols-4 gap-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  if (!summary) return <div className="text-center py-12 text-gray-400">Proyecto no encontrado</div>;

  const badge = HEALTH_BADGE(summary.pct_ejecutado);

  return (
    <div className="space-y-5" data-testid="project-detail-view">
      {/* Header + back */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button onClick={onBack}
            className="p-2 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 transition-all"
            data-testid="back-to-global-btn">
            <ChevronDown className="rotate-90" size={18} />
          </button>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{summary.project_name}</h2>
            <p className="text-xs text-gray-400">{summary.project_code || ""} · {summary.project_status}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-3 py-1.5 rounded-full font-semibold border ${badge.cls}`}>{badge.label}</span>
          <button onClick={handleExport} disabled={exporting}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-60"
            data-testid="export-project-excel-btn">
            {exporting ? <Loader2 size={15} className="animate-spin" /> : <FileSpreadsheet size={15} />}
            Exportar Excel
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Presupuesto Aprobado", value: formatPYGCompact(summary.budget_aprobado), sub: "Total presupuestado", icon: DollarSign, color: "blue" },
          { label: "Total Ejecutado", value: formatPYGCompact(summary.total_ejecutado), sub: `${summary.pct_ejecutado}% del presupuesto`, icon: TrendingUp, color: summary.pct_ejecutado >= 85 ? "red" : "orange" },
          { label: "Saldo Disponible", value: formatPYGCompact(Math.abs(summary.saldo)), sub: summary.saldo < 0 ? "⚠ Presupuesto superado" : "Disponible para gastar", icon: summary.saldo >= 0 ? ArrowUpRight : ArrowDownRight, color: summary.saldo >= 0 ? "emerald" : "red" },
          { label: "Ingresos Cobrados", value: formatPYGCompact(summary.total_ingresos), sub: `${summary.total_transacciones} transacciones`, icon: CheckCircle2, color: "purple" },
        ].map(c => (
          <div key={c.label} className={`bg-white rounded-xl border p-4 shadow-sm transition-all ${c.color === "red" ? "border-red-200" : "border-gray-200/60"}`}>
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${
              c.color === "blue" ? "bg-blue-50" : c.color === "red" ? "bg-red-50" :
              c.color === "orange" ? "bg-orange-50" : c.color === "purple" ? "bg-purple-50" : "bg-emerald-50"
            }`}>
              <c.icon size={18} className={
                c.color === "blue" ? "text-blue-600" : c.color === "red" ? "text-red-600" :
                c.color === "orange" ? "text-orange-500" : c.color === "purple" ? "text-purple-600" : "text-emerald-600"
              } />
            </div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{c.label}</p>
            <p className="text-lg font-bold text-gray-900 mt-0.5 tabular-nums">{c.value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{c.sub}</p>
          </div>
        ))}
      </div>

      {/* Progress bar */}
      <div className="bg-white rounded-xl border border-gray-200/60 p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-bold text-gray-700">Ejecución Presupuestaria</p>
          <span className={`text-sm font-bold ${PCT_COLOR(summary.pct_ejecutado)}`}>{summary.pct_ejecutado}%</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-4 relative overflow-hidden">
          <div
            className={`h-4 rounded-full transition-all ${summary.pct_ejecutado >= 100 ? "bg-red-500" : summary.pct_ejecutado >= 85 ? "bg-amber-400" : "bg-emerald-500"}`}
            style={{ width: `${Math.min(summary.pct_ejecutado, 100)}%` }}
          />
          {summary.alerta && (
            <div className="absolute right-2 top-0 h-4 flex items-center">
              <AlertTriangle size={11} className="text-amber-600" />
            </div>
          )}
        </div>
        {summary.alerta && (
          <div className="mt-2 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <AlertTriangle size={14} className="text-amber-600 flex-shrink-0" />
            <p className="text-xs text-amber-700 font-semibold">El gasto supera el 85% del presupuesto aprobado. Revisión recomendada.</p>
          </div>
        )}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-xl border border-gray-200/60 p-5 shadow-sm">
          <p className="text-sm font-bold text-gray-800 mb-4">Gasto por Categoría</p>
          <SemBarChart data={summary.categories} />
        </div>
        <div className="bg-white rounded-xl border border-gray-200/60 p-5 shadow-sm">
          <p className="text-sm font-bold text-gray-800 mb-4">Flujo Real Mensual</p>
          <MonthlyFlowChart data={summary.monthly} />
        </div>
      </div>

      {/* Category breakdown table */}
      <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50/50">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Desglose por Categoría</p>
        </div>
        <div className="divide-y divide-gray-50">
          {(summary.categories || []).map(cat => (
            <div key={cat.key}
              className={`flex items-center gap-4 px-5 py-3 hover:bg-gray-50/50 transition-colors cursor-pointer ${catFilter === cat.key ? "bg-blue-50/50" : ""}`}
              onClick={() => handleCatFilter(catFilter === cat.key ? "" : cat.key)}>
              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: cat.color }} />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-semibold text-gray-800">{cat.label}</p>
                  <p className="text-sm font-bold text-gray-900">{formatPYGCompact(cat.amount)}</p>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div className="h-1.5 rounded-full" style={{ width: `${cat.pct}%`, background: cat.color }} />
                </div>
              </div>
              <span className="text-xs font-bold text-gray-400 w-10 text-right">{cat.pct}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* Transactions list */}
      <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Transacciones del Proyecto</p>
            {catFilter && <p className="text-xs text-[#2E6DA4] mt-0.5">Filtrado por: {catFilter}</p>}
          </div>
          {catFilter && (
            <button onClick={() => handleCatFilter("")} className="text-xs text-gray-400 hover:text-gray-600 transition-colors underline">
              Quitar filtro
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                {["Fecha", "Categoría", "Descripción", "Proveedor", "N° Factura", "Monto"].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loadingTxn ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    {Array.from({ length: 6 }).map((__, j) => <td key={j} className="px-4 py-3"><Skeleton className="h-4 w-full" /></td>)}
                  </tr>
                ))
              ) : transactions.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-10 text-gray-400 text-sm">No hay transacciones registradas</td></tr>
              ) : transactions.slice(0, 100).map(txn => (
                <tr key={txn.id} className="border-b border-gray-50 hover:bg-gray-50/40">
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{String(txn.date || "").slice(0, 10)}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-blue-50 text-blue-700">{txn.category || "otros"}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 max-w-[200px] truncate">{txn.description || "—"}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{txn.supplier || "—"}</td>
                  <td className="px-4 py-3 text-xs font-mono text-gray-400">{txn.invoice_number || "—"}</td>
                  <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right whitespace-nowrap">
                    {formatPYGCompact(txn.amount_pyg || txn.amount || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {transactions.length > 100 && (
          <div className="px-5 py-3 border-t border-gray-100 text-xs text-gray-400 text-center">
            Mostrando 100 de {transactions.length} transacciones
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────
export default function FinanzasPorObra() {
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 min-h-full" data-testid="finanzas-por-obra-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
            Finanzas por Obra
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {selectedProjectId ? "Detalle financiero del proyecto" : "Resumen financiero por proyecto — presupuesto vs ejecutado"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selectedProjectId ? (
            <button onClick={() => setSelectedProjectId(null)}
              className="flex items-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 px-4 py-2.5 rounded-xl text-sm font-medium transition-all"
              data-testid="back-all-projects-btn">
              <Grid3X3 size={15} /> Todos los Proyectos
            </button>
          ) : null}
        </div>
      </div>

      {/* Content */}
      {selectedProjectId ? (
        <ProjectDetail
          projectId={selectedProjectId}
          onBack={() => setSelectedProjectId(null)}
        />
      ) : (
        <GlobalView onSelectProject={setSelectedProjectId} />
      )}
    </div>
  );
}
